import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import AudiotrackOutlinedIcon from '@mui/icons-material/AudiotrackOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import SmartDisplayOutlinedIcon from '@mui/icons-material/SmartDisplayOutlined';
import { getAllSeries, getAllActiveSubscriptions } from '../../../../services/series';
import { yogaTypes } from '../../../../Data/YogaTypes';
import { getAllMandalas } from '../../../../services/mandalas';
import { MandalaData } from '../Mandalas/MandalaData';
import { Series } from './VideoData';

export interface MediaMetadataFormData {
  title: string;
  description: string;
  tags: string[];
  duration: number;
  intensityLevel: string;
  seriesId: string;
  seriesEpisodeId: number | null;
  allowedSubscriptions: string[];
}

interface Props {
  handleNext: () => void;
  mediaType?: 'all' | 'video' | 'audio' | 'pdf';
  selectedMediaType: 'video' | 'audio' | 'pdf';
  onMediaTypeChange: (type: 'video' | 'audio' | 'pdf') => void;
  onSave: (metadata: MediaMetadataFormData) => void;
  initialData?: MediaMetadataFormData;
}

const defaultMetadata: MediaMetadataFormData = {
  title: '',
  description: '',
  tags: [],
  duration: 0,
  intensityLevel: '',
  seriesId: '',
  seriesEpisodeId: null,
  allowedSubscriptions: [],
};

const StepInformationForm: React.FC<Props> = ({
  handleNext,
  mediaType = 'all',
  selectedMediaType,
  onMediaTypeChange,
  onSave,
  initialData,
}) => {
  const [subscriptionList, setSubscriptionList] = useState<any[]>([]);
  const [seriesIdList, setSeriesIdList] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<MediaMetadataFormData>(initialData || defaultMetadata);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [subscriptionResponse, series, mandalas]: any[] = await Promise.all([
          getAllActiveSubscriptions(),
          getAllSeries(),
          getAllMandalas(),
        ]);

        const subscriptions = Array.isArray(subscriptionResponse?.data) ? subscriptionResponse.data : [];
        const seriesData: Series[] = Array.isArray(series?.data) ? series.data : [];
        const mandalasData: MandalaData[] = Array.isArray(mandalas?.data) ? mandalas.data : [];

        const allSeriesIds = [
          ...seriesData.map((item) => item.seriesId),
          ...mandalasData.map((item) => item.serieId),
        ].filter(Boolean);

        setSubscriptionList(subscriptions);
        setSeriesIdList(Array.from(new Set(allSeriesIds)));
      } catch (loadError: any) {
        setError(loadError?.message || 'No se pudieron cargar los datos del formulario.');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const mediaLabel = useMemo(() => {
    if (selectedMediaType === 'audio') return 'Audio';
    if (selectedMediaType === 'pdf') return 'PDF';
    return 'Video';
  }, [selectedMediaType]);

  const mediaIcon = useMemo(() => {
    if (selectedMediaType === 'audio') return <AudiotrackOutlinedIcon fontSize="small" />;
    if (selectedMediaType === 'pdf') return <PictureAsPdfOutlinedIcon fontSize="small" />;
    return <SmartDisplayOutlinedIcon fontSize="small" />;
  }, [selectedMediaType]);

  const requiresDuration = selectedMediaType === 'video';

  const handleMediaTypeSelect = (type: 'video' | 'audio' | 'pdf') => {
    onMediaTypeChange(type);
    setFormData((prevData) => ({
      ...prevData,
      duration: type === 'video' ? prevData.duration : 0,
      intensityLevel: type === 'video' ? prevData.intensityLevel : '',
      seriesEpisodeId: type === 'video' ? prevData.seriesEpisodeId : null,
    }));
  };

  const handleInputChange = (event: any) => {
    const { name, value } = event.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: name === 'duration' || name === 'seriesEpisodeId' ? Number(value) : value,
    }));
  };

  const tagOptions = useMemo(() => yogaTypes.map((item) => item.title), []);

  const handleTagsChange = (_event: any, values: string[]) => {
    setFormData((prevData) => ({
      ...prevData,
      tags: values
        .map((value) => String(value || '').trim())
        .filter(Boolean),
    }));
  };

  const handleAllowedSubscriptionsChange = (_event: any, values: { planId: string }[]) => {
    setFormData((prevData) => ({
      ...prevData,
      allowedSubscriptions: values.map((value) => value.planId),
    }));
  };

  const isValid =
    formData.title.trim().length > 0 &&
    formData.description.trim().length > 0 &&
    (!requiresDuration || formData.duration > 0) &&
    formData.seriesId.trim().length > 0 &&
    formData.allowedSubscriptions.length > 0;

  const handleContinue = () => {
    onSave(formData);
    handleNext();
  };

  return (
    <Box sx={{ color: 'black' }}>
      <div className="mb-5 rounded-xl border border-[#d8ecee] bg-[#f4f6f8] p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e5355' }}>
              Información de contenido
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Define primero la metadata para validar y publicar correctamente.
            </Typography>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full border border-[#b0d8dc] bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] text-[#2e5355]">
            {mediaIcon}
            {mediaLabel}
          </span>
        </div>
      </div>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {mediaType === 'all' && (
          <FormControl fullWidth>
            <InputLabel>Tipo de Media</InputLabel>
            <Select
              value={selectedMediaType}
              label="Tipo de Media"
              onChange={(event) => handleMediaTypeSelect(event.target.value as 'video' | 'audio' | 'pdf')}
            >
              <MenuItem value="video">Video</MenuItem>
              <MenuItem value="audio">Audio</MenuItem>
              <MenuItem value="pdf">PDF</MenuItem>
            </Select>
          </FormControl>
        )}

        <TextField
          fullWidth
          label={`Título ${mediaLabel}`}
          name="title"
          value={formData.title}
          onChange={handleInputChange}
        />

        {requiresDuration ? (
          <TextField
            fullWidth
            label="Duración (segundos)"
            type="number"
            name="duration"
            value={formData.duration}
            onChange={handleInputChange}
            InputProps={{ inputProps: { min: 0, step: 5 } }}
          />
        ) : (
          <div className="rounded-lg border border-[#d8ecee] bg-[#f4f6f8] px-3 py-2 text-sm text-[#475569]">
            Duración: automática (no requerida para {mediaLabel}).
          </div>
        )}

        <FormControl fullWidth>
          <InputLabel>Series ID</InputLabel>
          <Select name="seriesId" value={formData.seriesId} label="Series ID" onChange={handleInputChange}>
            {seriesIdList.map((seriesId) => (
              <MenuItem key={seriesId} value={seriesId}>
                {seriesId}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedMediaType === 'video' && (
          <>
            <FormControl fullWidth>
              <InputLabel>Intensidad</InputLabel>
              <Select
                name="intensityLevel"
                value={formData.intensityLevel}
                label="Intensidad"
                onChange={handleInputChange}
              >
                <MenuItem value="Principiante">Principiante</MenuItem>
                <MenuItem value="Intermedio">Intermedio</MenuItem>
                <MenuItem value="Avanzado">Avanzado</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Episodio"
              type="number"
              name="seriesEpisodeId"
              value={formData.seriesEpisodeId || 0}
              onChange={handleInputChange}
              InputProps={{ inputProps: { min: 1, max: 100 } }}
            />
          </>
        )}

        <div className="md:col-span-2">
          <TextField
            fullWidth
            label="Descripción"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            multiline
            minRows={3}
          />
        </div>

        <div className="md:col-span-2">
          <FormControl fullWidth>
            <Autocomplete
              multiple
              freeSolo
              options={tagOptions}
              value={formData.tags}
              onChange={handleTagsChange}
              loading={isLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  label="Tags"
                  placeholder="Meditación, Respira, Yin"
                />
              )}
            />
          </FormControl>
        </div>

        <div className="md:col-span-2">
          <FormControl fullWidth>
            <Autocomplete
              multiple
              options={subscriptionList}
              getOptionLabel={(option: any) => option.planId}
              onChange={handleAllowedSubscriptionsChange}
              loading={isLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  label="Subscripciones permitidas"
                  placeholder="MUJER_SOBERANA"
                />
              )}
            />
          </FormControl>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          variant="contained"
          color="success"
          onClick={handleContinue}
          disabled={!isValid || isLoading}
          className="!rounded-lg !px-5 !py-2"
        >
          Continuar a carga
        </Button>
      </div>
    </Box>
  );
};

export default StepInformationForm;
